"use client";

import { App } from "@capacitor/app";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { markNotificationAsRead } from "@/actions/notification";
import {
  ACTIVITY_ACTION_EVENT,
  type HiiveActivityAction,
  type HiiveActivityKind,
} from "@/hooks/use-live-activity-commands";
import {
  captureAcquisitionEvent,
  DEFERRED_DEEP_LINK,
  observeAcquisitionUrl,
  takeDeferredDeepLink,
} from "@/lib/acquisition/client";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { createClientLogger } from "@/lib/logging/client";
import {
  HIIVE_HOST_ALIASES,
  requiresDocumentNavigation,
} from "@/lib/native/app-links";
import {
  claimNativeColdStartLink,
  hasCommandParam,
  markLaunchUrlHandled,
  normalizeInternalPath,
  recordDocumentNavigation,
  shouldHandleLaunchUrl,
  shouldSkipDocumentNavigation,
} from "@/lib/native/deep-link-navigation";
import {
  isNativePlatform,
  releaseNativeSplashGate,
} from "@/lib/native/native-splash-gate";
import { getQueryClient } from "@/lib/query-client-registry";
import { resolveShortLinkPath } from "@/lib/short-links/resolve-client";

const ACTIVITY_ACTIONS = new Set<HiiveActivityAction>([
  "completeSet",
  "pause",
  "resume",
  "end",
  "skipRest",
  "addRest",
  "subtractRest",
]);

// A `?action=` on a hiive:// deep link (iOS 16 fallback + notification tap)
// routes through the same command pipeline as native App Intents / broadcasts.
const dispatchActivityActionFromUrl = (url: URL) => {
  const action = url.searchParams.get("action");
  if (!action || !ACTIVITY_ACTIONS.has(action as HiiveActivityAction)) return;
  const host = `${url.host}${url.pathname}`;
  const activity: HiiveActivityKind = host.includes("weight-training")
    ? "workout"
    : "movement";
  // Delay so the destination screen has mounted its command listener.
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent(ACTIVITY_ACTION_EVENT, {
        detail: {
          activity,
          action,
          id: crypto.randomUUID(),
          sessionId: url.searchParams.get("sessionId") ?? undefined,
          setId: url.searchParams.get("setId") ?? undefined,
          at: Date.now(),
        },
      }),
    );
  }, 700);
};

/** What Capacitor reports for a notification swiped away (not tapped). */
const NOTIFICATION_DISMISS_ACTION = "dismiss";

const logger = createClientLogger("hooks/use-deep-link-handler");

// A human tap reads the notification-center row. Chat message receipts still
// require a visible viewport (or an accepted explicit reply), never delivery.
const acknowledgeNotificationTap = (value: unknown) => {
  const id = typeof value === "number" ? value : Number(value);
  if (!Number.isSafeInteger(id) || id <= 0) return;
  void markNotificationAsRead(id)
    .then(async (result) => {
      if (!result.ok) return;
      const client = getQueryClient();
      if (!client) return;
      await Promise.all([
        client.invalidateQueries({ queryKey: ["notifications"] }),
        client.invalidateQueries({ queryKey: ["unreadNotificationCount"] }),
        client.invalidateQueries({ queryKey: ["native-widget-stats"] }),
      ]);
    })
    .catch(() => {
      logger.warn("Notification tap acknowledgment failed", {
        notification_id: id,
      });
    });
};

const describeDeepLink = (rawUrl: string) => {
  if (rawUrl.startsWith("/")) {
    return { kind: "relative", path: rawUrl.split(/[?#]/, 1)[0] || "/" };
  }

  try {
    const url = new URL(rawUrl);
    return {
      kind: url.protocol.replace(":", "") || "unknown",
      host: url.hostname || url.host,
      path: url.pathname || "/",
      has_query: url.search.length > 0,
      has_hash: url.hash.length > 0,
    };
  } catch {
    return { kind: "invalid", length: rawUrl.length };
  }
};

const appendNotificationReplyDraft = (rawUrl: string, draft: string) => {
  if (!draft.trim()) return rawUrl;

  try {
    const base =
      rawUrl.startsWith("/") && typeof window !== "undefined"
        ? window.location.origin
        : undefined;
    const url = new URL(rawUrl, base);
    url.searchParams.set("notification_reply", draft);
    url.searchParams.set("notification_reply_nonce", String(Date.now()));

    if (rawUrl.startsWith("/")) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  } catch {
    return rawUrl;
  }
};

/**
 * Hook to handle deep links from:
 * - Live Activity taps (hiive://path)
 * - Notification taps (via extra.url)
 *
 * Usage: Add this hook to your root layout or _app
 */
export function useDeepLinkHandler() {
  const router = useRouter();
  // Whether anything has claimed this launch. Set by `navigateToInternalPath`
  // so the `getLaunchUrl()` fallback below can tell a launch `appUrlOpen`
  // already claimed from one it must still handle.
  const navigationPendingRef = useRef(false);

  useEffect(() => {
    if (!isNativePlatform()) return;

    let cancelled = false;
    let appUrlListener: { remove: () => Promise<void> } | null = null;
    let notificationListener: { remove: () => Promise<void> } | null = null;
    let pushNotificationListener: { remove: () => Promise<void> } | null = null;

    /**
     * Client-navigate without holding the splash: the native shell already
     * dismissed it on the first painted frame (see native-splash-ready). On
     * a cold start the shell has normally loaded the destination itself
     * (claimNativeColdStartLink), so this only runs for warm links and for
     * binaries that predate preemption.
     */
    const pushToTarget = (target: string) => {
      releaseNativeSplashGate();
      router.push(target);
    };

    // Returns true when a navigation was started. The caller uses it to skip
    // the `getLaunchUrl()` fallback for a launch `appUrlOpen` (or a
    // notification tap) already claimed — not to hold any splash.
    const navigateToInternalPath = async (target: string): Promise<boolean> => {
      const normalizedTarget = normalizeInternalPath(target);
      const normalizedCurrent = normalizeInternalPath(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );

      if (normalizedTarget === normalizedCurrent) return false;

      // /r/, /c/, /s/ and /l/ are Route Handlers rather than React pages.
      // Route Handlers "do not participate in layouts or client-side
      // navigations" (Next docs), so the client router expects an RSC payload
      // and cannot resolve them.
      if (requiresDocumentNavigation(normalizedTarget)) {
        // Guard first, so a double-delivered cold start is one navigation
        // whichever way it is ultimately followed.
        if (shouldSkipDocumentNavigation(normalizedTarget)) return false;

        // Past the dedupe every branch below navigates, so claim the launch
        // BEFORE the resolve await — otherwise the `getLaunchUrl()` fallback
        // below can observe an unclaimed launch mid-resolve and follow the
        // same link a second time.
        navigationPendingRef.current = true;

        // A short link is a pure redirect — no Set-Cookie, nothing a document
        // navigation would pick up that this does not. Resolving it to its
        // destination turns the cold-start deep link into an ordinary client
        // navigation and saves a second full app boot. `/r/` and `/c/` are NOT
        // resolvable this way: their attribution and coupon cookies are only
        // committed by a real document navigation, so they fall through (as
        // does anything whose resolution fails, times out, or 404s).
        //
        // The resolve runs in the background: the splash is already gone (it
        // released on first paint), so a slow lookup only delays the
        // navigation, never the first content.
        const resolved = await resolveShortLinkPath(normalizedTarget);
        if (resolved) {
          // The native shell may already have loaded the resolved destination
          // as the WebView's start URL (item 8: cold-start preemption). Pushing
          // the page we are already on would only refetch it, so claim the
          // launch and stand down. Query strings participate: a reply draft or
          // action on the target still differs and still navigates.
          if (
            normalizeInternalPath(resolved) ===
            normalizeInternalPath(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            )
          ) {
            return true;
          }
          pushToTarget(resolved);
          return true;
        }

        // `replace` rather than `assign`: the short URL is never a place Back
        // should return to (it would just redirect forward again — a
        // back-button trap), and it matches Next `redirect()`'s default.
        //
        // The remaining guards in `@/lib/native/deep-link-navigation` apply
        // here: the storm breaker safe-harbors to `/` rather than ever looping.
        if (recordDocumentNavigation()) {
          logger.error("[DeepLink] redirect storm — safe-harboring to /", {
            target: normalizedTarget,
          });
          window.location.replace("/");
          return true;
        }
        window.location.replace(normalizedTarget);
        return true;
      }

      navigationPendingRef.current = true;
      pushToTarget(normalizedTarget);
      return true;
    };

    /** Resolves true when a navigation is in flight. See navigateToInternalPath. */
    let lastTrackedLink = "";
    let lastTrackedLinkAt = 0;
    const navigateFromUrl = async (rawUrl: string): Promise<boolean> => {
      if (!rawUrl) return false;
      observeAcquisitionUrl(rawUrl);
      if (rawUrl !== lastTrackedLink || Date.now() - lastTrackedLinkAt > 5000) {
        lastTrackedLink = rawUrl;
        lastTrackedLinkAt = Date.now();
        captureAcquisitionEvent(ANALYTICS_EVENTS.DEEP_LINK_OPENED, {
          ...describeDeepLink(rawUrl),
          is_deferred: false,
          provider: "capacitor",
        });
      }

      // The iOS shell already loaded this link as the first document (cold
      // start), and this is its retained echo. Following it again would
      // re-run a redirect handler (/r/, /c/, /l/) — or just refetch the page.
      // A command link (`?action=`) still runs: the shell loaded its
      // destination WITHOUT the command, and the page executes it from here.
      if (claimNativeColdStartLink(rawUrl) && !hasCommandParam(rawUrl)) {
        navigationPendingRef.current = true;
        return true;
      }

      try {
        if (rawUrl.startsWith("/")) {
          return await navigateToInternalPath(rawUrl);
        }

        const url = new URL(rawUrl);

        if (url.protocol === "hiive:") {
          const host = HIIVE_HOST_ALIASES[url.host] ?? url.host;
          const path = `${host}${url.pathname}${url.search}${url.hash}`;
          const pending = path ? await navigateToInternalPath(path) : false;
          dispatchActivityActionFromUrl(url);
          return pending;
        }

        if (
          typeof window !== "undefined" &&
          (url.origin === window.location.origin ||
            url.hostname === window.location.hostname)
        ) {
          return await navigateToInternalPath(
            `${url.pathname}${url.search}${url.hash}`,
          );
        }

        // Off-origin: Capacitor ejects this to the system browser and the
        // WebView stays where it is, so there is no in-app navigation to start.
        window.location.href = rawUrl;
        return false;
      } catch (error) {
        logger.warn("[DeepLink] Failed to parse URL:", {
          ...describeDeepLink(rawUrl),
          error,
        });
        return false;
      }
    };

    const setupListeners = async () => {
      try {
        // `appUrlOpen` is attached FIRST, before any other async work. It is
        // the primary path, as in Capacitor's own deep-link guide, which
        // handles cold starts with this listener and nothing else: the event
        // is dispatched with `retainUntilConsumed` on both platforms
        // (AppPlugin.swift `handleUrlOpened` / `handleUniversalLink`,
        // AppPlugin.java `EVENT_URL_OPEN` — verified against the installed
        // @capacitor/app sources), so a link that launched the app is queued
        // natively and delivered the moment this listener attaches.
        // Registering IS the delivery trigger, so no grace sleep is needed
        // before consulting the fallback below: if `appUrlOpen` claims the
        // launch, `navigationPendingRef` is set before the fallback runs.
        //
        // `App` is a static import (not a dynamic chunk) for the same reason:
        // a cold-start link must not wait for a chunk fetch to be observed.
        // The notification plugins stay dynamic — they are only needed for
        // tap handling, never for first paint.
        appUrlListener = await App.addListener("appUrlOpen", (event) => {
          logger.debug("[DeepLink] URL opened", describeDeepLink(event.url));
          // Record, but do not gate: a `/r/`, `/c/` or `/s/` link does a
          // document navigation, and the next document's `getLaunchUrl()`
          // fallback reads the same URL back out of native state — without
          // this it would follow the link a second time.
          markLaunchUrlHandled(event.url);
          void navigateFromUrl(event.url);
        });
        if (cancelled) return;

        // Start the notification chunks fetching now, in parallel with the
        // listener work below. Every cold-start tap event this hook cares
        // about is queued natively until its listener attaches.
        const notificationPlugins = Promise.all([
          import("@capacitor/local-notifications"),
          import("@capacitor/push-notifications"),
        ]);
        // Awaited below; this only marks the rejection handled so a failure
        // here cannot surface as an unhandled rejection while the remaining
        // setup is still running.
        notificationPlugins.catch(() => {});

        const [{ LocalNotifications }, { PushNotifications }] =
          await notificationPlugins;
        if (cancelled) return;

        notificationListener = await LocalNotifications.addListener(
          "localNotificationActionPerformed",
          (notification) => {
            // Swiping a notification away arrives here too (iOS categories use
            // `.customDismissAction`); it must never navigate the app.
            if (notification.actionId === NOTIFICATION_DISMISS_ACTION) return;
            acknowledgeNotificationTap(
              notification.notification.extra?.notificationId,
            );
            logger.debug("[DeepLink] Notification tapped", {
              action_id: notification.actionId,
              has_extra: Boolean(notification.notification.extra),
            });

            // Get the URL from notification extra data
            const url = notification.notification.extra?.url;
            if (url) {
              // The foreground mirror carries the chat category, so its Reply
              // action lands here when the native send falls back; keep the
              // typed text the same way the push handler below does.
              const inputValue =
                typeof notification.inputValue === "string"
                  ? notification.inputValue.trim()
                  : "";
              const targetUrl =
                notification.actionId === "HIIVE_REPLY" && inputValue
                  ? appendNotificationReplyDraft(String(url), inputValue)
                  : String(url);
              logger.debug(
                "[DeepLink] Navigating to",
                describeDeepLink(targetUrl),
              );
              void navigateFromUrl(targetUrl);
            }
          },
        );

        pushNotificationListener = await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action) => {
            if (action.actionId === NOTIFICATION_DISMISS_ACTION) return;
            acknowledgeNotificationTap(
              action.notification.data?.notificationId,
            );
            const url = action.notification.data?.url;
            const data = (action.notification.data ?? {}) as Record<
              string,
              unknown
            >;

            // iOS keeps the category inside `aps` (see use-foreground-push).
            const category =
              data.category ??
              (data.aps as Record<string, unknown> | undefined)?.category;
            captureClientEvent(ANALYTICS_EVENTS.PUSH_NOTIFICATION_TAPPED, {
              notification_category:
                typeof category === "string" ? category : undefined,
              notification_type:
                typeof data.notificationType === "string"
                  ? data.notificationType
                  : undefined,
              action_id: action.actionId,
              // The destination path only — push payload URLs can carry
              // tokens in the query string.
              destination:
                typeof url === "string" ? url.split("?", 1)[0] : undefined,
            });

            if (typeof url === "string" && url) {
              const inputValue =
                typeof action.inputValue === "string"
                  ? action.inputValue.trim()
                  : "";
              const targetUrl =
                action.actionId === "HIIVE_REPLY" && inputValue
                  ? appendNotificationReplyDraft(url, inputValue)
                  : url;
              logger.debug(
                "[DeepLink] Push notification tapped",
                describeDeepLink(targetUrl),
              );
              void navigateFromUrl(targetUrl);
            }
          },
        );

        logger.debug("[DeepLink] Listeners set up");

        // Fallback only. `getLaunchUrl()` re-reads the same cold-start URL on
        // every document reload, which is what looped `/l/` forever, so it is
        // consulted solely for the case `appUrlOpen` cannot cover: the plugin
        // registers its notification observers in `load()`, and a universal
        // link delivered before that has nobody listening and is never
        // retained. `shouldHandleLaunchUrl` keeps it to once per session.
        // Skipped outright when `appUrlOpen` (or a notification tap) already
        // claimed the launch above.
        if (cancelled) return;
        if (!navigationPendingRef.current) {
          const launchUrl = await App.getLaunchUrl();
          if (launchUrl?.url && shouldHandleLaunchUrl(launchUrl.url)) {
            logger.debug(
              "[DeepLink] Launch URL fallback",
              describeDeepLink(launchUrl.url),
            );
            await navigateFromUrl(launchUrl.url);
          }
        }

        // The splash itself released on first paint (see
        // native-splash-ready.client.tsx); this only settles the gate for any
        // waiter, and is a no-op once already released.
        if (cancelled) return;
        releaseNativeSplashGate();
      } catch (error) {
        logger.warn("[DeepLink] Failed to set up listeners:", error);
        // Never strand a gate waiter because a plugin import failed.
        releaseNativeSplashGate();
      }
    };

    setupListeners();

    // A Meta ad install's deferred link arrives after consent switches the SDK
    // on, possibly before this hook mounted; drain the queue now and on signal.
    const followDeferredLink = () => {
      const path = takeDeferredDeepLink();
      if (!path || cancelled) return;
      captureAcquisitionEvent(ANALYTICS_EVENTS.DEEP_LINK_OPENED, {
        ...describeDeepLink(path),
        is_deferred: true,
        provider: "meta_sdk",
      });
      observeAcquisitionUrl(path);
      void navigateToInternalPath(path);
    };
    followDeferredLink();
    window.addEventListener(DEFERRED_DEEP_LINK, followDeferredLink);

    return () => {
      cancelled = true;
      window.removeEventListener(DEFERRED_DEEP_LINK, followDeferredLink);
      appUrlListener?.remove();
      notificationListener?.remove();
      pushNotificationListener?.remove();
    };
  }, [router]);
}
